import { getFormCategory, type FormCategory } from './registry.js';
import { logger } from '../utils/logger.js';

type FormHandler = (form: HTMLFormElement, formId: string, category: FormCategory) => void;

/**
 * Sélecteur CSS des formulaires HSFC sur lesquels Trackeez attache un handler.
 * Classe `hsfc-Form` = posée par le SDK HSFC React (nouveau format HubSpot).
 * `data-form-id` = UUID du form HubSpot, source de vérité pour la catégorisation.
 */
const HSFC_FORM_SELECTOR = 'form.hsfc-Form[data-form-id]';

/**
 * Scanne le DOM actuel + observe les mutations futures pour détecter
 * TOUS les formulaires HSFC (sélecteur CSS global).
 * Appelle `handler` une fois par formulaire trouvé.
 *
 * Un attribut data-trackeez-attached est posé pour éviter les doubles-attaches.
 */
export function watchForForms(handler: FormHandler): void {
  // 1. Scanner le DOM actuel
  scanExistingForms(handler);

  // 2. Observer les ajouts futurs (formulaires hydratés après coup par React)
  const observer = new MutationObserver(() => scanExistingForms(handler));
  observer.observe(document.body, { childList: true, subtree: true });
}

function scanExistingForms(handler: FormHandler): void {
  const forms = document.querySelectorAll<HTMLFormElement>(HSFC_FORM_SELECTOR);
  for (const form of forms) {
    if (form.dataset['trackeezAttached']) continue;

    const formId = form.dataset['formId'];
    if (!formId) continue; // ceinture + bretelles, le sélecteur CSS garantit déjà sa présence

    form.dataset['trackeezAttached'] = 'true';
    const category = getFormCategory(formId);

    if (category === 'unknown') {
      logger.warn(
        `⚠️ Form non catégorisé — ajouter à FORM_REGISTRY dans src/forms/registry.ts: ${formId}`,
      );
    }
    logger.info(`Form détecté: ${formId} (${category})`);

    handler(form, formId, category);
  }
}
